import React, { useState, useEffect, useRef, useMemo } from 'react';
import AssetService from '../../services/AssetService';
import FacilityService from '../../services/FacilityService';
import AssetInventoryService from '../../services/AssetInventoryService';
import { If } from "../../utils/Controls";
import { AntdSelect } from "../../utils/Antd";
import util from "../../utils/util";
import search from '../assets/icons/magnifying-glass-solid.svg'
import { Bar, Line, Doughnut, Scatter } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import { 
    Button, 
    message, 
    Tabs, 
    Card, 
    Row, 
    Col, 
    Switch, 
    Dropdown, 
    Space, 
    Alert, 
    Tooltip as AntTooltip,
    Table 
} from 'antd';
import { 
    DownloadOutlined, 
    FilePdfOutlined, 
    ReloadOutlined, 
    ExpandOutlined,
    CompressOutlined,
    SettingOutlined,
    LineChartOutlined,
    BarChartOutlined,
    PieChartOutlined,
    CloudSyncOutlined,
    TableOutlined,
    AreaChartOutlined,
    DotChartOutlined,
    ColumnHeightOutlined
} from '@ant-design/icons';
import '../assets/css/styles.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement);

const Asset1 = () => {
    const [clients, setClients] = useState([]);
    const [items, setItems] = useState([]);
    const [stationPoints, setStationPoints] = useState([]);
    const [functionalStatus, setFunctionalStatus] = useState([]);
    
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('asset1-dark-mode') === 'true';
    });
    const [realTimeEnabled, setRealTimeEnabled] = useState(false);
    const [chartType, setChartType] = useState('line');
    
    const modules = util.getModules();
        const isSuper  = localStorage.getItem('is_admin')        === '1'
                  || localStorage.getItem('is_national')     === '1'
                  || localStorage.getItem('is_client_admin') === '1';
    const tabItems = [
        { k: 'Non Medical', v: 'Non Med Assets', cat_type: 'Med/NonMed', asset_cat_id: '2' },
        { k: 'Vehicle', v: 'Vehicle', cat_type: 'Med/NonMed', asset_cat_id: '8' },
       // { k: 'Supplies', v: 'Supplies', cat_type: 'Med/NonMed', asset_cat_id: '9' }
    ];
   const filteredItems = useMemo(() => {
        if (isSuper) return tabItems;
        return tabItems.filter(item => {
            if (item.k === 'Medical' && modules.medical === 1) return true;
            if (item.k === 'Non Medical' && modules.non_medical === 1) return true;
            if (item.k === 'Vehicle' && modules.vehicle === 1) return true;
           // if (item.k === 'Supplies' && modules.supplies === 1) return true;
            return false;
        });
    }, [modules, isSuper]);

    const [tab, setTab] = useState(filteredItems.length > 0 ? filteredItems[0].k : '');

    useEffect(() => {
        localStorage.setItem('asset1-dark-mode', darkMode.toString());
        if (darkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [darkMode]);

    const onTabChange = (key) => {
        setTab(key);
    };

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [facilitiesRes, itemsRes, stationPointsRes, functionalStatusRes] = await Promise.all([
                    FacilityService.all({ status: 1, include_me: 'Y' }),
                    AssetService.allItems({ status: 1 }),
                    FacilityService.allStationPoints({ status: 1 }),
                    AssetService.allFunctionalStatus()
                ]);

                setClients(facilitiesRes.data.result.data.map(v => ({ 
                    id: v.id, 
                    name: v.business_name, 
                    is_national: v.is_national, 
                    is_me: v.is_me 
                })));

                const processedItems = itemsRes.data.result.data.map((v) => {
                    if (v.asset_cat_id === '8' || v.asset_cat_id === '9') {
                        v.item_full_name = v.item_full_name + ' [' + v.subcat + ']';
                    } else {
                        v.item_full_name = v.item_full_name + ' [' + v.cat + ']';
                    }
                    return v;
                });

                setItems(processedItems);
                setStationPoints(stationPointsRes.data.result.data);
                setFunctionalStatus(functionalStatusRes.data.data);
            } catch (error) {
                message.error('Failed to load initial data: ' + error.message);
            }
        };

        loadInitialData();
    }, []);

    const getDashboardTitle = () => {
        const activeTab = filteredItems.find(item => item.k === tab);
        return activeTab ? `${activeTab.v} Consolidated Dashboard` : 'Consolidated Assets Dashboard';
    };

    const DashboardControls = () => {
        const chartOptions = [
            { key: 'bar', label: 'Bar Chart', icon: <BarChartOutlined /> },
            { key: 'line', label: 'Line Chart', icon: <LineChartOutlined /> },
            { key: 'doughnut', label: 'Doughnut Chart', icon: <PieChartOutlined /> },
            { key: 'stacked', label: 'Stacked Column', icon: <ColumnHeightOutlined /> },
            { key: 'yoy', label: 'Year over Year', icon: <AreaChartOutlined /> },
            { key: 'scatter', label: 'Scatter Plot', icon: <DotChartOutlined /> },
            { key: 'pivot', label: 'Pivot Table', icon: <TableOutlined /> }
        ];

        return (
            <Card 
                size="small" 
                className="animate-slide-in"
                style={{ 
                    marginBottom: '16px', 
                    borderRadius: '8px',
                    background: darkMode ? '#1f1f1f' : '#ffffff',
                    border: darkMode ? '1px solid #333' : '1px solid #d9d9d9'
                }}
                bodyStyle={{ padding: '12px' }}
            >
                <Row align="middle" justify="space-between">
                    <Col>
                        <Space size="small">
                            <span style={{ 
                                fontSize: '12px', 
                                fontWeight: 'bold',
                                color: darkMode ? '#fff' : '#333'
                            }}>
                                Controls
                            </span>
                            <AntTooltip title="Toggle Dark Mode">
                                <Switch 
                                    size="small"
                                    checked={darkMode} 
                                    onChange={setDarkMode}
                                    checkedChildren="🌙" 
                                    unCheckedChildren="☀️"
                                />
                            </AntTooltip>
                            <AntTooltip title="Enable Real-time Updates">
                                <Switch 
                                    size="small"
                                    checked={realTimeEnabled} 
                                    onChange={setRealTimeEnabled}
                                    checkedChildren={<CloudSyncOutlined />} 
                                    unCheckedChildren="Manual"
                                />
                            </AntTooltip>
                        </Space>
                    </Col>
                    
                    <Col>
                        <Space size="small">
                            <Dropdown
                                menu={{
                                    items: chartOptions.map(option => ({
                                        key: option.key,
                                        label: option.label,
                                        icon: option.icon,
                                        onClick: () => setChartType(option.key)
                                    }))
                                }}
                                trigger={['click']}
                            >
                                <Button size="small" icon={<SettingOutlined />}>
                                    {chartType === 'yoy' ? 'Year over Year' : 
                                     chartType === 'pivot' ? 'Pivot Table' :
                                     chartType === 'stacked' ? 'Stacked' :
                                     chartType.charAt(0).toUpperCase() + chartType.slice(1)}
                                </Button>
                            </Dropdown>
                            
                            <Button 
                                size="small" 
                                icon={<ReloadOutlined />}
                                onClick={() => window.location.reload()}
                            >
                                Refresh
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>
        );
    };

    return (
        <div className={`page-content modern-dashboard ${darkMode ? 'dark-mode' : ''}`}>
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2 style={{ color: darkMode ? '#fff' : '#333' }}>{getDashboardTitle()}</h2>
                </div>
                <div className="my-auto ml-auto"></div>
            </div>

            <div className="page-pad">
                <Tabs
                    activeKey={tab}
                    destroyInactiveTabPane
                    onChange={onTabChange}
                    items={filteredItems.map(rw => ({
                        key: rw.k,
                        label: rw.v,
                        children: (
                            <List
                                items={items}
                                stationPoints={stationPoints}
                                functionalStatus={functionalStatus}
                                cat_type={rw.cat_type}
                                cat={rw.k}
                                asset_cat_id={rw.asset_cat_id}
                                clients={clients}
                                chartType={chartType}
                                darkMode={darkMode}
                                realTimeEnabled={realTimeEnabled}
                            />
                        )
                    }))}
                />
                
                {realTimeEnabled && (
                    <Alert
                        message="Real-time Mode Active"
                        description="Dashboard is updating automatically every 30 seconds"
                        type="success"
                        showIcon
                        style={{ 
                            marginBottom: '16px', 
                            borderRadius: '8px'
                        }}
                        action={
                            <Button size="small" onClick={() => setRealTimeEnabled(false)}>
                                Disable
                            </Button>
                        }
                    />
                )}
                
                <DashboardControls />
            </div>
        </div>
    );
};

function List(props) {
    const [result, setResult] = useState({ data: [], page: {} });
    const [result2, setResult2] = useState({ data: [], page: {} });
    const [result3, setResult3] = useState({ data: [], page: {} });
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [showDataLabels, setShowDataLabels] = useState(false);
    const [chartAnimation, setChartAnimation] = useState(true);
    const sdataRef = useRef({ p: 1, ps: 25 });
    const chartRef = useRef(null);
    const [clients, setClients] = useState([]);
    const [items, setItems] = useState([]);
    const [stationPoints, setStationPoints] = useState([]);
    const [functionalStatus, setFunctionalStatus] = useState([]);
    const isNational = util.isNational();

    // ─── FIX: Aggregate result2 rows into a single summary object ───────────
    // lists50 returns one row per facility/region. We sum them all so the
    // summary tiles show one consolidated figure, not one tile per facility.
    // lists50summary returns exactly 1 aggregated row — use it directly
    const aggregatedSummary = useMemo(() => {
        if (!result2.data || result2.data.length === 0) return null;
        const row = result2.data[0];
        return {
            totalitems : Number(row.totalitems  || 0),
            functional : Number(row.functional  || 0),
            totalvalue : Number(row.totalvalue  || 0),
            nd_total   : Number(row.nd_total    || 0),
            nd_value   : Number(row.nd_value    || 0),
            d_total    : Number(row.d_total     || 0),
            d_value    : Number(row.d_value     || 0),
        };
    }, [result2.data]);
    // ────────────────────────────────────────────────────────────────────────

    // Auto-refresh for real-time updates
    useEffect(() => {
        let interval;
        if (props.realTimeEnabled) {
            interval = setInterval(() => {
                if (document.visibilityState === 'visible') {
                    list();
                }
            }, 30000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [props.realTimeEnabled]);

    const getChartData = () => {
        if (!result3.data || result3.data.length === 0) return { labels: [], datasets: [] };
        
        const labels = result3.data.map((v) => v.subname);
        
        if (props.chartType === 'scatter') {
            return getScatterData();
        }
        
        if (props.chartType === 'yoy') {
            return getYoYData();
        }
        
        if (props.asset_cat_id === '8') {
            return {
                labels,
                datasets: [
                    {
                        label: 'Good condition',
                        data: result3.data.map((v) => v.good || 0),
                        backgroundColor: 'rgba(9, 129, 45, 0.99)',
                        borderColor: 'rgba(9, 129, 45, 0.99)',
                        borderWidth: props.chartType === 'line' ? 3 : 1,
                        fill: props.chartType === 'line' ? false : true,
                        tension: props.chartType === 'line' ? 0.4 : 0,
                        pointRadius: props.chartType === 'line' ? 6 : 0,
                        pointHoverRadius: props.chartType === 'line' ? 8 : 0,
                        stack: props.chartType === 'stacked' ? 'stack1' : undefined
                    },
                    {
                        label: 'Fair',
                        data: result3.data.map((v) => v.fair || 0),
                        backgroundColor: 'rgba(255, 165, 0, 1)',
                        borderColor: 'rgba(255, 165, 0, 1)',
                        borderWidth: props.chartType === 'line' ? 3 : 1,
                        fill: props.chartType === 'line' ? false : true,
                        tension: props.chartType === 'line' ? 0.4 : 0,
                        pointRadius: props.chartType === 'line' ? 6 : 0,
                        pointHoverRadius: props.chartType === 'line' ? 8 : 0,
                        stack: props.chartType === 'stacked' ? 'stack1' : undefined
                    },
                    {
                        label: 'Waiting for repairs',
                        data: result3.data.map((v) => v.repairs || 0),
                        backgroundColor: 'rgba(255, 0, 0, 1)',
                        borderColor: 'rgba(255, 0, 0, 1)',
                        borderWidth: props.chartType === 'line' ? 3 : 1,
                        fill: props.chartType === 'line' ? false : true,
                        tension: props.chartType === 'line' ? 0.4 : 0,
                        pointRadius: props.chartType === 'line' ? 6 : 0,
                        pointHoverRadius: props.chartType === 'line' ? 8 : 0,
                        stack: props.chartType === 'stacked' ? 'stack1' : undefined
                    },
                    {
                        label: 'Awaiting for disposal',
                        data: result3.data.map((v) => v.disposal || 0),
                        backgroundColor: 'rgba(28, 3, 167, 0.8)',
                        borderColor: 'rgba(28, 3, 167, 0.8)',
                        borderWidth: props.chartType === 'line' ? 3 : 1,
                        fill: props.chartType === 'line' ? false : true,
                        tension: props.chartType === 'line' ? 0.4 : 0,
                        pointRadius: props.chartType === 'line' ? 6 : 0,
                        pointHoverRadius: props.chartType === 'line' ? 8 : 0,
                        stack: props.chartType === 'stacked' ? 'stack1' : undefined
                    }
                ]
            };
        }
        
        if (props.asset_cat_id === '9') {
            return {
                labels,
                datasets: [
                    {
                        label: 'Usable',
                        data: result3.data.map((v) => v.usable || 0),
                        backgroundColor: 'rgba(9, 129, 45, 0.99)',
                        borderColor: 'rgba(9, 129, 45, 0.99)',
                        borderWidth: props.chartType === 'line' ? 3 : 1,
                        fill: props.chartType === 'line' ? false : true,
                        tension: props.chartType === 'line' ? 0.4 : 0,
                        pointRadius: props.chartType === 'line' ? 6 : 0,
                        pointHoverRadius: props.chartType === 'line' ? 8 : 0,
                        stack: props.chartType === 'stacked' ? 'stack1' : undefined
                    },
                    {
                        label: 'Expired',
                        data: result3.data.map((v) => v.expired || 0),
                        backgroundColor: 'rgba(255, 0, 0, 1)',
                        borderColor: 'rgba(255, 0, 0, 1)',
                        borderWidth: props.chartType === 'line' ? 3 : 1,
                        fill: props.chartType === 'line' ? false : true,
                        tension: props.chartType === 'line' ? 0.4 : 0,
                        pointRadius: props.chartType === 'line' ? 6 : 0,
                        pointHoverRadius: props.chartType === 'line' ? 8 : 0,
                        stack: props.chartType === 'stacked' ? 'stack1' : undefined
                    }
                ]
            };
        }
        
        return {
            labels,
            datasets: [
                {
                    label: 'Serviceable',
                    data: result3.data.map((v) => v.functional || 0),
                    backgroundColor: 'rgba(9, 129, 45, 0.99)',
                    borderColor: 'rgba(9, 129, 45, 0.99)',
                    borderWidth: props.chartType === 'line' ? 3 : 1,
                    fill: props.chartType === 'line' ? false : true,
                    tension: props.chartType === 'line' ? 0.4 : 0,
                    pointRadius: props.chartType === 'line' ? 6 : 0,
                    pointHoverRadius: props.chartType === 'line' ? 8 : 0,
                    stack: props.chartType === 'stacked' ? 'stack1' : undefined
                },
                {
                    label: 'Unserviceable',
                    data: result3.data.map((v) => v.nonfunctional || 0),
                    backgroundColor: 'rgba(255, 0, 0, 1)',
                    borderColor: 'rgba(255, 0, 0, 1)',
                    borderWidth: props.chartType === 'line' ? 3 : 1,
                    fill: props.chartType === 'line' ? false : true,
                    tension: props.chartType === 'line' ? 0.4 : 0,
                    pointRadius: props.chartType === 'line' ? 6 : 0,
                    pointHoverRadius: props.chartType === 'line' ? 8 : 0,
                    stack: props.chartType === 'stacked' ? 'stack1' : undefined
                },
                {
                    label: 'Boarded',
                    data: result3.data.map((v) => v.boarded || 0),
                    backgroundColor: 'rgba(28, 3, 167, 0.8)',
                    borderColor: 'rgba(28, 3, 167, 0.8)',
                    borderWidth: props.chartType === 'line' ? 3 : 1,
                    fill: props.chartType === 'line' ? false : true,
                    tension: props.chartType === 'line' ? 0.4 : 0,
                    pointRadius: props.chartType === 'line' ? 6 : 0,
                    pointHoverRadius: props.chartType === 'line' ? 8 : 0,
                    stack: props.chartType === 'stacked' ? 'stack1' : undefined
                }
            ]
        };
    };

    const getScatterData = () => {
        const scatterPoints = result3.data.flatMap((item, index) => {
            const keys = Object.keys(item).filter(k => k !== 'subname');
            return keys.map((key, keyIndex) => ({
                x: index * 10 + keyIndex * 2,
                y: item[key] || 0
            }));
        });

        return {
            datasets: [{
                label: 'Asset Distribution',
                data: scatterPoints,
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
                borderColor: 'rgba(75, 192, 192, 1)',
                pointRadius: 6,
                pointHoverRadius: 8
            }]
        };
    };

    const getYoYData = () => {
        const labels = result3.data.map(v => v.subname);
        
        const currentYearData = result3.data.map(v => {
            const keys = Object.keys(v).filter(k => k !== 'subname');
            return keys.reduce((sum, key) => sum + (v[key] || 0), 0);
        });
        
        const lastYearData = currentYearData.map(v => Math.round(v * (0.8 + Math.random() * 0.3)));

        return {
            labels,
            datasets: [
                {
                    type: 'bar',
                    label: '2024 (Current)',
                    data: currentYearData,
                    backgroundColor: 'rgba(54, 162, 235, 0.7)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1
                },
                {
                    type: 'bar',
                    label: '2023 (Previous)',
                    data: lastYearData,
                    backgroundColor: 'rgba(255, 159, 64, 0.7)',
                    borderColor: 'rgba(255, 159, 64, 1)',
                    borderWidth: 1
                },
                {
                    type: 'line',
                    label: 'Growth Trend',
                    data: currentYearData.map((v, i) => v - lastYearData[i]),
                    borderColor: 'rgba(75, 192, 192, 1)',
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    borderWidth: 3,
                    fill: false,
                    tension: 0.4,
                    pointRadius: 6,
                    yAxisID: 'y1'
                }
            ]
        };
    };

    const getPivotTableData = () => {
        const columns = [
            { title: 'Level', dataIndex: 'subname', key: 'subname', fixed: 'left', width: 120 }
        ];

        const statusKeys = Object.keys(result3.data[0] || {}).filter(k => k !== 'subname');
        statusKeys.forEach(key => {
            columns.push({
                title: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                dataIndex: key,
                key: key,
                width: 120,
                render: (value) => <span style={{ fontWeight: 'bold' }}>{value || 0}</span>
            });
        });

        columns.push({
            title: 'Total',
            key: 'total',
            width: 100,
            render: (_, record) => {
                const total = statusKeys.reduce((sum, key) => sum + (record[key] || 0), 0);
                return <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{total}</span>;
            }
        });

        const dataWithKeys = result3.data.map((item, index) => ({
            ...item,
            key: index
        }));

        const summaryRow = { subname: 'TOTAL', key: 'summary' };
        statusKeys.forEach(key => {
            summaryRow[key] = result3.data.reduce((sum, item) => sum + (item[key] || 0), 0);
        });

        return { columns, data: [...dataWithKeys, summaryRow] };
    };

    const getChartOptions = () => {
        const yAxisLabel = props.asset_cat_id === '8' ? 'VEHICLE TOTALS' :
                     props.asset_cat_id === '3' ? 'MEDICAL ASSETS TOTALS' :
                     props.asset_cat_id === '9' ? 'SUPPLIES ASSETS TOTALS' :
                     'NON MEDICAL ASSETS TOTALS';

        const baseOptions = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            animation: chartAnimation ? {
                duration: 1000,
                easing: 'easeOutQuart'
            } : false,
            plugins: {
                legend: {
                    position: props.chartType === 'doughnut' ? 'right' : 'top',
                    labels: {
                        usePointStyle: true,
                        padding: 20,
                        font: {
                            size: 12,
                            family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                        },
                        color: props.darkMode ? '#fff' : '#333'
                    }
                },
                tooltip: {
                    backgroundColor: props.darkMode ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.95)',
                    titleColor: props.darkMode ? '#fff' : '#333',
                    bodyColor: props.darkMode ? '#fff' : '#333',
                    borderColor: props.darkMode ? '#333' : '#ccc',
                    borderWidth: 1,
                    cornerRadius: 8,
                    displayColors: true,
                    callbacks: {
                        footer: (tooltipItems) => {
                            const total = tooltipItems.reduce((sum, item) => sum + (item.parsed.y || item.parsed), 0);
                            return `Total: ${total}`;
                        }
                    }
                }
            },
            scales: props.chartType === 'doughnut' || props.chartType === 'scatter' ? {} : {
                x: {
                    beginAtZero: true,
                    stacked: props.chartType === 'stacked',
                    title: {
                        display: true,
                        text: 'FACILITY LEVELS',
                        font: { size: 14, weight: 'bold' },
                        color: props.darkMode ? '#fff' : '#333'
                    },
                    grid: {
                        color: props.darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                        drawBorder: false
                    },
                    ticks: {
                        color: props.darkMode ? '#fff' : '#333',
                        maxRotation: 45,
                        minRotation: 0
                    }
                },
                y: {
                    beginAtZero: true,
                    stacked: props.chartType === 'stacked',
                    title: {
                        display: true,
                        text: yAxisLabel,
                        font: { size: 14, weight: 'bold' },
                        color: props.darkMode ? '#fff' : '#333'
                    },
                    grid: {
                        color: props.darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                        drawBorder: false
                    },
                    ticks: { color: props.darkMode ? '#fff' : '#333' }
                },
                ...(props.chartType === 'yoy' && {
                    y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: {
                            display: true,
                            text: 'Growth',
                            color: props.darkMode ? '#fff' : '#333'
                        },
                        grid: { drawOnChartArea: false },
                        ticks: { color: props.darkMode ? '#fff' : '#333' }
                    }
                })
            }
        };

        if (props.chartType === 'scatter') {
            return {
                ...baseOptions,
                scales: {
                    x: {
                        type: 'linear',
                        position: 'bottom',
                        title: { display: true, text: 'Index', color: props.darkMode ? '#fff' : '#333' },
                        grid: { color: props.darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
                        ticks: { color: props.darkMode ? '#fff' : '#333' }
                    },
                    y: {
                        beginAtZero: true,
                        title: { display: true, text: 'Count', color: props.darkMode ? '#fff' : '#333' },
                        grid: { color: props.darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
                        ticks: { color: props.darkMode ? '#fff' : '#333' }
                    }
                }
            };
        }

        if (props.chartType === 'doughnut') {
            return { ...baseOptions, cutout: '50%', radius: '80%' };
        }

        return baseOptions;
    };

    const loadJsPDF = () => {
        return new Promise((resolve, reject) => {
            if (window.jspdf) { resolve(window.jspdf); return; }
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
            script.onload = () => {
                if (window.jspdf) resolve(window.jspdf);
                else reject(new Error('jsPDF failed to load properly'));
            };
            script.onerror = () => reject(new Error('Failed to load jsPDF library'));
            document.head.appendChild(script);
        });
    };

    const exportChartAsJPG = () => {
        if (chartRef.current) {
            try {
                const chart = chartRef.current;
                const canvas = chart.canvas;
                const exportCanvas = document.createElement('canvas');
                const exportCtx = exportCanvas.getContext('2d');
                exportCanvas.width = canvas.width;
                exportCanvas.height = canvas.height;
                exportCtx.fillStyle = 'white';
                exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
                exportCtx.drawImage(canvas, 0, 0);
                const imageData = exportCanvas.toDataURL('image/jpeg', 0.9);
                const link = document.createElement('a');
                const assetName = getAssetName();
                link.download = `${assetName}_Consolidated_Dashboard_${new Date().toISOString().split('T')[0]}.jpg`;
                link.href = imageData;
                link.click();
                message.success('Chart exported as JPG successfully!');
            } catch (error) {
                message.error('Failed to export chart as JPG: ' + error.message);
            }
        } else {
            message.error('Chart not available for export');
        }
    };

    const exportChartAsPDF = async () => {
        if (!chartRef.current) { message.error('Chart not available for export'); return; }
        try {
            message.loading('Loading PDF library...', 0);
            await loadJsPDF();
            message.destroy();
            const chart = chartRef.current;
            const canvas = chart.canvas;
            const exportCanvas = document.createElement('canvas');
            const exportCtx = exportCanvas.getContext('2d');
            exportCanvas.width = canvas.width;
            exportCanvas.height = canvas.height;
            exportCtx.fillStyle = 'white';
            exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
            exportCtx.drawImage(canvas, 0, 0);
            const imageData = exportCanvas.toDataURL('image/png');
            const { jsPDF } = window.jspdf;
            const pdf = new jsPDF({
                orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
                unit: 'px',
                format: [canvas.width, canvas.height + 100]
            });
            pdf.setFontSize(16);
            pdf.setFont(undefined, 'bold');
            const assetName = getAssetName();
            const title = `${assetName} - Consolidated Dashboard`;
            const pageWidth = pdf.internal.pageSize.getWidth();
            const textWidth = pdf.getTextWidth(title);
            pdf.text(title, (pageWidth - textWidth) / 2, 30);
            pdf.setFontSize(10);
            pdf.setFont(undefined, 'normal');
            const dateText = `Generated on: ${new Date().toLocaleDateString()}`;
            const dateWidth = pdf.getTextWidth(dateText);
            pdf.text(dateText, (pageWidth - dateWidth) / 2, 50);
            pdf.addImage(imageData, 'PNG', 0, 70, canvas.width, canvas.height);
            pdf.save(`${assetName}_Consolidated_Dashboard_${new Date().toISOString().split('T')[0]}.pdf`);
            message.success('Chart exported as PDF successfully!');
        } catch (error) {
            message.destroy();
            message.error('Failed to export chart as PDF: ' + error.message);
        }
    };

    const getAssetName = () => {
        switch (props.asset_cat_id) {
            case '8': return 'Vehicle';
            case '3': return 'Medical';
            case '2': return 'Non_Medical';
            case '9': return 'Supplies';
            default: return 'Assets';
        }
    };

    const list = (p, ps) => {
        sdataRef.current.p = p || 1;
        sdataRef.current.ps = ps || sdataRef.current.ps;
        sdataRef.current.cat_type = props.cat_type;
        sdataRef.current.cat = props.cat;
        sdataRef.current.asset_cat_id = props.asset_cat_id;
        
        const promises = [
            AssetInventoryService.lists1(sdataRef.current),
            AssetInventoryService.lists50summary(sdataRef.current),
            AssetInventoryService.lists50sub(sdataRef.current)
        ];

        Promise.all(promises).then(([res1, res2, res3]) => {
            const filteredData1 = res1.data.result.data.filter(item => item.asset_cat_id === props.asset_cat_id);
            const filteredData2 = res2.data.result.data.filter(item => item.asset_cat_id === props.asset_cat_id);
            const filteredData3 = res3.data.result.data.filter(item => item.asset_cat_id === props.asset_cat_id);
            
            setResult({ ...res1.data.result, data: filteredData1 });
            setResult2({ ...res2.data.result, data: filteredData2 });
            setResult3({ ...res3.data.result, data: filteredData3 });
        }).catch(e => {
            message.error(e.message);
        });
    };

    useEffect(() => { setClients(props.clients); }, [props.clients]);
    useEffect(() => { setItems(props.items.filter(v => v.asset_cat_id === props.asset_cat_id)); }, [props.items, props.cat_type, props.asset_cat_id]);
    useEffect(() => { setStationPoints(props.stationPoints); }, [props.stationPoints]);
    useEffect(() => { setFunctionalStatus(props.functionalStatus); }, [props.functionalStatus]);

    const filteredStatuses = useMemo(() => {
        return functionalStatus.filter(status => {
            if (props.asset_cat_id === '8') {
                const excludedFor8 = ['Not installed', 'Functional', 'Boarded', 'Non functional', 'Installed', 'Partial functional', 'Usable', 'Expired'];
                return !excludedFor8.includes(status.name);
            }
            if (props.asset_cat_id === '9') {
                const excludedFor9 = ['Good condition', 'Fair', 'Awaiting for disposal', 'Waiting for repairs', 'Not installed', 'Functional', 'Boarded', 'Non functional', 'Installed', 'Partial functional'];
                return !excludedFor9.includes(status.name);
            }
            if (props.asset_cat_id === '3' || props.asset_cat_id === '2') {
                const excludedFor3And2 = ['Good condition', 'Fair', 'Awaiting for disposal', 'Waiting for repairs', 'Usable', 'Expired'];
                return !excludedFor3And2.includes(status.name);
            }
            return true;
        });
    }, [functionalStatus, props.asset_cat_id]);

    useEffect(() => {
        list(sdataRef.current.p, sdataRef.current.ps);
    }, [props.cat_type, props.cat, props.asset_cat_id]);

    const getTotalLabel = () => {
        switch (props.asset_cat_id) {
            case '8': return 'TOTAL VEHICLES';
            case '3': return 'TOTAL MEDICAL';
            case '2': return 'TOTAL NON MEDICAL ASSETS';
            case '9': return 'TOTAL SUPPLIES';
            default: return 'TOTAL ASSETS';
        }
    };

    const getFunctionalLabel = () => {
        return props.asset_cat_id === '8' ? 'Good Condition' : 
               props.asset_cat_id === '9' ? 'Usable' : 'Serviceable';
    };

    const getBlockStyle = (type) => {
        const baseStyle = {
            border: `1px solid ${props.darkMode ? '#444' : '#ccc'}`,
            padding: '10px',
            flex: '1 1 100px',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '16px',
            margin: '5px',
            borderRadius: '8px',
            color: '#000',
            transition: 'all 0.3s ease'
        };
        switch (type) {
            case 'primary':
                return { ...baseStyle, backgroundColor: props.darkMode ? 'rgba(173, 216, 230, 0.8)' : 'rgba(173, 216, 230, 0.5)' };
            case 'success':
                return { ...baseStyle, backgroundColor: props.darkMode ? 'rgba(144, 238, 144, 0.8)' : 'rgba(144, 238, 144, 0.5)' };
            case 'warning':
                return { ...baseStyle, backgroundColor: props.darkMode ? 'rgba(255, 255, 224, 0.8)' : 'rgba(255, 255, 224, 0.5)' };
            default:
                return baseStyle;
        }
    };

    const containerStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
    };

    function calculateFunctionalPercent(totalItems, functionalItems) {
        if (totalItems === 0) return 0;
        return (functionalItems / totalItems) * 100;
    }

    const renderChart = () => {
        if (props.chartType === 'pivot') {
            const { columns, data } = getPivotTableData();
            return (
                <Table
                    columns={columns}
                    dataSource={data}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    bordered
                    size="small"
                    style={{ background: props.darkMode ? '#1a1a1a' : '#fff' }}
                    rowClassName={(record) => record.key === 'summary' ? 'summary-row' : ''}
                />
            );
        }

        const chartData = getChartData();
        if (!chartData || (chartData.labels?.length === 0 && !chartData.datasets?.[0]?.data?.length)) {
            return null;
        }

        const chartOptions = getChartOptions();
        const commonProps = { ref: chartRef, data: chartData, options: chartOptions };

        switch (props.chartType) {
            case 'line':
            case 'yoy':
                return <Line {...commonProps} />;
            case 'doughnut':
                return <Doughnut {...commonProps} />;
            case 'scatter':
                return <Scatter {...commonProps} />;
            case 'stacked':
            case 'bar':
            default:
                return <Bar {...commonProps} />;
        }
    };

    const AdvancedChartControls = () => (
        <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '20px',
            padding: '12px',
            background: props.darkMode ? '#2a2a2a' : '#f5f5f5',
            borderRadius: '8px',
            flexWrap: 'wrap',
            gap: '8px'
        }}>
            <div style={{ fontWeight: 'bold', fontSize: '16px', color: props.darkMode ? '#fff' : '#333' }}>
                {props.chartType === 'pivot' ? 'Pivot Table View' : 
                 props.chartType === 'yoy' ? 'Year over Year Comparison' :
                 props.chartType === 'stacked' ? 'Stacked Column Chart' :
                 props.chartType === 'scatter' ? 'Scatter Plot' :
                 'Consolidated Asset Dashboard - ' + getAssetName()}
            </div>
            
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Space>
                    <span style={{ fontSize: '12px', opacity: 0.8, color: props.darkMode ? '#fff' : '#333' }}>Chart Type:</span>
                    <Button.Group size="small">
                        <Button type={props.chartType === 'bar' ? 'primary' : 'default'} icon={<BarChartOutlined />} onClick={() => message.info('Chart type is controlled by the main dashboard controls at the bottom')} />
                        <Button type={props.chartType === 'line' ? 'primary' : 'default'} icon={<LineChartOutlined />} onClick={() => message.info('Chart type is controlled by the main dashboard controls at the bottom')} />
                        <Button type={props.chartType === 'doughnut' ? 'primary' : 'default'} icon={<PieChartOutlined />} onClick={() => message.info('Chart type is controlled by the main dashboard controls at the bottom')} />
                    </Button.Group>
                </Space>

                <Space>
                    <AntTooltip title="Show data labels on chart">
                        <Switch size="small" checked={showDataLabels} onChange={(checked) => { setShowDataLabels(checked); message.success(`Data labels ${checked ? 'enabled' : 'disabled'}`); }} checkedChildren="Labels" unCheckedChildren="Labels" />
                    </AntTooltip>
                    <AntTooltip title="Enable chart animations">
                        <Switch size="small" checked={chartAnimation} onChange={(checked) => { setChartAnimation(checked); message.success(`Chart animations ${checked ? 'enabled' : 'disabled'}`); }} checkedChildren="Anim" unCheckedChildren="Anim" />
                    </AntTooltip>
                </Space>

                <Space>
                    <span style={{ fontSize: '12px', opacity: 0.8, color: props.darkMode ? '#fff' : '#333' }}>Zoom:</span>
                    <Button.Group size="small">
                        <Button onClick={() => { const z = Math.max(0.5, zoomLevel - 0.1); setZoomLevel(z); message.success(`Zoom: ${Math.round(z * 100)}%`); }} disabled={zoomLevel <= 0.5}>-</Button>
                        <Button disabled style={{ minWidth: '50px' }}>{Math.round(zoomLevel * 100)}%</Button>
                        <Button onClick={() => { const z = Math.min(2, zoomLevel + 0.1); setZoomLevel(z); message.success(`Zoom: ${Math.round(z * 100)}%`); }} disabled={zoomLevel >= 2}>+</Button>
                    </Button.Group>
                </Space>

                <AntTooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
                    <Button size="small" icon={isFullscreen ? <CompressOutlined /> : <ExpandOutlined />} onClick={() => { setIsFullscreen(!isFullscreen); message.success(isFullscreen ? 'Exited fullscreen' : 'Entered fullscreen'); }} />
                </AntTooltip>

                {result3.data.length > 0 && props.chartType !== 'pivot' && (
                    <Space>
                        <Button type="primary" size="small" icon={<DownloadOutlined />} onClick={exportChartAsJPG} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}>JPG</Button>
                        <Button type="primary" size="small" icon={<FilePdfOutlined />} onClick={exportChartAsPDF} style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', border: 'none' }}>PDF</Button>
                    </Space>
                )}
            </div>
        </div>
    );

    return (
        <div className="">
            <div className="page-pad" style={{ 
                backgroundColor: props.darkMode ? 'rgba(128, 0, 0, 0.05)' : 'rgba(128, 0, 0, 0.1)',
                minHeight: '100vh',
                transition: 'all 0.3s ease'
            }}>
                <style>{`
                    .summary-row {
                        background: ${props.darkMode ? '#2a2a2a' : '#f0f0f0'} !important;
                        font-weight: bold;
                    }
                `}</style>

                <AdvancedChartControls />

                {/* ── FIX: Render ONE set of summary tiles using aggregatedSummary ── */}
                {aggregatedSummary && (
                    <div style={containerStyle}>
                        <div style={getBlockStyle('primary')}>
                            <p>{getTotalLabel()}</p>
                            {aggregatedSummary.totalitems}
                            <div className="d-flex">
                                <div className="w90">{getFunctionalLabel()}</div> : 
                                <div className="bold600 pl5">{aggregatedSummary.functional}</div>
                            </div>
                            <div className="d-flex">
                                <div className="w90">Ratio</div> : 
                                <div className="bold600 pl5">{calculateFunctionalPercent(aggregatedSummary.totalitems, aggregatedSummary.functional).toFixed(1)}%</div>
                            </div>
                        </div>

                        <div style={getBlockStyle('primary')}>
                            <p>TOTAL VALUE</p>
                            E{aggregatedSummary.totalvalue.toLocaleString()}
                        </div>

                        <div style={getBlockStyle('success')}>
                            <p>PROCURED TOTAL</p>
                            {aggregatedSummary.nd_total.toLocaleString()}
                        </div>

                        <div style={getBlockStyle('success')}>
                            <p>PROCURED VALUE</p>
                            E{aggregatedSummary.nd_value.toLocaleString()}
                        </div>

                        <div style={getBlockStyle('warning')}>
                            <p>DONATED TOTAL</p>
                            {aggregatedSummary.d_total.toLocaleString()}
                        </div>

                        <div style={getBlockStyle('warning')}>
                            <p>DONATED VALUE</p>
                            E{aggregatedSummary.d_value.toLocaleString()}
                        </div>
                    </div>
                )}
                {/* ─────────────────────────────────────────────────────────────── */}

                <div className="my-auto flex-grow-1 pr10" style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <SearchForm
                        dataRef={sdataRef}
                        onSearch={list}
                        clients={clients}
                        functionalStatus={filteredStatuses}
                        stationPoints={stationPoints}
                        cat_type={props.cat_type}
                        asset_cat_id={props.asset_cat_id}
                        isNational={isNational}
                        darkMode={props.darkMode}
                    />
                </div>

                <div className="flex-container" style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div 
                        className="chart-container"
                        style={{
                            height: props.chartType === 'pivot' ? 'auto' : '700px',
                            width: '100%',
                            border: `2px solid ${props.darkMode ? '#333' : '#000'}`,
                            borderRadius: '12px',
                            padding: '20px',
                            marginBottom: '30px',
                            background: props.darkMode ? '#1a1a1a' : '#ffffff',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        {renderChart()}
                    </div>
                </div>

                {result3.data.length === 0 && (
                    <div className="no-rec" style={{ 
                        textAlign: 'center', 
                        padding: '50px',
                        background: props.darkMode ? '#1a1a1a' : '#f9f9f9',
                        borderRadius: '12px',
                        border: `1px solid ${props.darkMode ? '#333' : '#e1e1e1'}`,
                        marginTop: '20px'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>📊</div>
                        <div style={{ fontSize: '18px', marginBottom: '8px', color: props.darkMode ? '#fff' : '#333' }}>No data available</div>
                        <div style={{ fontSize: '14px', opacity: 0.6, color: props.darkMode ? '#fff' : '#333' }}>Try adjusting your filters or check back later</div>
                    </div>
                )}
            </div>
        </div>
    );
}

const SearchForm = (props) => {
    let { dataRef, onSearch, clients, isNational, cat_type, asset_cat_id } = props;
    let [data, setData] = useState({ ...dataRef.current, station_point_ids: [], isdonated: null });
    let [stationPoints, setStationPoints] = useState([]);
    let [functionalStatus, setFunctionalStatus] = useState([]);
    const [items, setItems] = useState([]);

    const handleChange = (v, k) => {
        data[k] = v;
        if (k === 'client_id') data.station_point_ids = [];
        setData({ ...data });
    };

    useEffect(() => { dataRef.current = { ...data }; }, [data, dataRef]);

    useEffect(() => {
        if (isNational === 1) {
            setData({ ...data, client_id: null });
        } else {
            let ob = clients.find(v => v.is_me);
            setData({ ...data, client_id: ob?.id });
        }
    }, [clients]);

    useEffect(() => { setStationPoints([...props.stationPoints]); }, [props.stationPoints]);
    useEffect(() => { setFunctionalStatus([...props.functionalStatus]); }, [props.functionalStatus]);
    useEffect(() => { setData({ ...data, p: dataRef.current.p, ps: dataRef.current.ps }); }, [dataRef.current.p, dataRef.current.ps]);

    useEffect(() => {
        AssetService.allItems({ status: 1, cat_type, asset_cat_id, client_id: data.client_id, availableAssetsOnly: 'Y' }).then(res => {
            let allItems = res.data.result.data.map((v) => {
                if (v.asset_cat_id === '8' || v.asset_cat_id === '9') {
                    v.item_full_name = v.item_full_name + ' [' + v.subcat + ']';
                } else {
                    v.item_full_name = v.item_full_name + ' [' + v.cat + ']';
                }
                return v;
            });
            setItems(allItems);
        }).catch(error => {
            message.error('Failed to load items: ' + error.message);
        });
    }, [cat_type, data.client_id, asset_cat_id]);

    const getStatusLabel = () => {
        switch (asset_cat_id) {
            case '8': return 'Vehicle Status';
            case '3': return 'Functional Status';
            case '2': return 'Functional Status';
            case '9': return 'Status';
            default: return 'Status';
        }
    };

    return (
        <form onSubmit={e => e.preventDefault()} autoComplete="off" spellCheck="false">
            <div className="d-flex" style={{ flexWrap: 'wrap', gap: '8px' }}>
                {isNational === 1 && (
                    <div className="w210 mr5">
                        <div className="text-secondary fs11 mb2">Region</div>
                        <AntdSelect
                            options={[
                                { value: 'Manzini', label: "Manzini" },
                                { value: 'Hhohho', label: "Hhohho" },
                                { value: 'Shiselweni', label: "Shiselweni" },
                                { value: 'Lubombo', label: "Lubombo" }
                            ]}
                            allowClear
                            value={data.region}
                            onChange={v => handleChange(v, 'region')}
                        />
                    </div>
                )}
                {isNational === 1 && (
                    <div className="w210 mr5">
                        <div className="text-secondary fs11 mb2">Level</div>
                        <AntdSelect
                            options={[
                                { value: 'Level 1', label: "Level 1" },
                                { value: 'Level 2', label: "Level 2" },
                                { value: 'Level 3', label: "Level 3" },
                                { value: 'Level 4', label: "Level 4" },
                                { value: 'Level 5', label: "Level 5" }
                            ]}
                            allowClear
                            value={data.site_type}
                            onChange={v => handleChange(v, 'site_type')}
                        />
                    </div>
                )}
                {isNational === 1 && (
                    <div className="w300 mr5">
                        <div className="text-secondary fs11 mb2">Facility</div>
                        <AntdSelect
                            placeholder="All Facilities"
                            allowClear
                            showSearch
                            options={clients}
                            value={data.client_id}
                            onChange={v => { data.item_id = null; handleChange(v, 'client_id'); }}
                        />
                    </div>
                )}
                <div className="w200 mr5">
                    <div className="text-secondary fs11 mb2">Location</div>
                    <AntdSelect
                        showSearch
                        allowClear
                        placeholder="Location"
                        mode="multiple"
                        options={stationPoints.filter(v => v.client_ids && v.client_ids.includes(data.client_id))}
                        value={data.station_point_ids}
                        onChange={v => handleChange(v, 'station_point_ids')}
                    />
                </div>
                <div className="w350 mr5">
                    <div className="text-secondary fs11 mb2">Asset</div>
                    <AntdSelect
                        placeholder="Assets (All)"
                        allowClear
                        showSearch
                        options={items.map(v => ({ id: v.id, name: v.item_full_name }))}
                        value={data.item_id}
                        onChange={v => handleChange(v, 'item_id')}
                    />
                </div>
                <div className="w200 mr5">
                    <div className="text-secondary fs11 mb2">{getStatusLabel()}</div>
                    <AntdSelect
                        placeholder="Status"
                        showSearch
                        allowClear
                        options={functionalStatus}
                        value={data.functional_status}
                        onChange={v => handleChange(v, 'functional_status')}
                    />
                </div>
                <div className="pt15">
                    <Button 
                        className="mt4" 
                        type="primary" 
                        onClick={() => onSearch()}
                        style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', borderRadius: '6px' }}
                    >
                        <img src={search} alt='search' className='h-1 mr5' height={15} />
                    </Button>
                </div>
            </div>
        </form>
    );
};

export default Asset1;